import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingCart, Menu, X, Sparkles, Heart, Mail, Phone, User, LogOut, LogIn, DollarSign, Bell } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { useCurrency } from "../context/CurrencyContext";
import { LOGO_URL, api } from "../lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/bundles", label: "Bundles" },
  { to: "/digital-shop", label: "Digital Shop" },
  { to: "/shipping", label: "Shipping" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const { currency, toggle } = useCurrency();
  const [open, setOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [bellOpen, setBellOpen] = React.useState(false);
  const [activePromos, setActivePromos] = React.useState([]);
  const [seenPromos, setSeenPromos] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem("c4t_seen_promos") || "[]"); } catch { return []; }
  });
  const [notifications, setNotifications] = React.useState([]);
  const [notifsRead, setNotifsRead] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => { setOpen(false); setMenuOpen(false); setBellOpen(false); }, [location.pathname]);

  // Fetch active promos on mount and every 5 minutes
  React.useEffect(() => {
    const fetchPromos = () => api.get("/promos/active").then(r => setActivePromos(r.data)).catch(() => {});
    fetchPromos();
    const interval = setInterval(fetchPromos, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch push notifications every 30s (logged-in users only)
  React.useEffect(() => {
    if (!user) { setNotifications([]); return; }
    const fetchNotifs = () =>
      api.get("/notifications").then(r => {
        setNotifications(r.data || []);
      }).catch(() => {});
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  const unseenPromos = activePromos.filter(p => !seenPromos.includes(p.code)).length;
  const unreadNotifs = notifications.filter(n => !n.is_read).length;
  const totalBadge = unseenPromos + unreadNotifs;

  const showBell = activePromos.length > 0 || !!user; // always show bell when logged in

  const markAllSeen = () => {
    const all = activePromos.map(p => p.code);
    setSeenPromos(all);
    try { localStorage.setItem("c4t_seen_promos", JSON.stringify(all)); } catch {}
  };

  const markNotifsRead = () => {
    if (!user || unreadNotifs === 0) return;
    api.post("/notifications/read-all").then(() => {
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    }).catch(() => {});
  };

  const openBell = () => {
    const opening = !bellOpen;
    setBellOpen(opening);
    if (opening) {
      markAllSeen();
      markNotifsRead();
    }
    setMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/95 border-b-[3px] border-black" data-testid="site-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src={LOGO_URL} alt="Cheap4Teach" className="h-12 w-12 object-contain logo-blend wiggle" />
          <span className="font-['Fredoka'] font-bold text-xl sm:text-2xl text-black hidden sm:block">
            Cheap<span className="text-[#FF1F87]">4</span>Teach
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-6">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-['Fredoka'] font-semibold text-lg c4t-link-underline ${isActive ? "text-[#FF1F87]" : "text-black"}`
              }>{n.label}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button onClick={toggle} className="c4t-pill !text-sm" data-testid="currency-toggle" title={`Switch to ${currency === "USD" ? "JMD" : "USD"}`}>
            <DollarSign className="w-4 h-4" /> {currency}
          </button>
          {user ? (
            <div className="relative">
              <button onClick={() => setMenuOpen(!menuOpen)} className="c4t-pill" data-testid="nav-account-button">
                {user.picture ? <img src={user.picture} alt="" className="w-5 h-5 rounded-full" /> : <User className="w-5 h-5" strokeWidth={2.5} />}
                <span className="hidden sm:inline truncate max-w-[100px]">{user.name?.split(" ")[0]}</span>
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white border-[3px] border-black rounded-2xl shadow-[4px_4px_0_#000] overflow-hidden z-50">
                  <Link to="/account" onClick={() => setMenuOpen(false)} className="block px-4 py-2 hover:bg-[#FFF9E6] font-semibold" data-testid="nav-account-link">My Orders</Link>
                  <button onClick={logout} className="w-full text-left px-4 py-2 hover:bg-[#FFF9E6] font-semibold flex items-center gap-2" data-testid="nav-logout-btn">
                    <LogOut className="w-4 h-4" /> Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" className="c4t-pill" data-testid="nav-login-button">
              <LogIn className="w-4 h-4" /> <span className="hidden sm:inline">Sign In</span>
            </Link>
          )}

          {/* Unified notification bell — promos + admin push notifications */}
          {showBell && (
            <div className="relative">
              <button onClick={openBell} className="relative c4t-pill !p-2" data-testid="nav-bell-button" title="Notifications & deals">
                <Bell className="w-5 h-5" strokeWidth={2.5} />
                {totalBadge > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#FF1F87] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center border-2 border-black" data-testid="bell-badge">
                    {totalBadge}
                  </span>
                )}
              </button>
              {bellOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white border-[3px] border-black rounded-2xl shadow-[4px_4px_0_#000] overflow-hidden z-50" data-testid="bell-dropdown">
                  {/* Push notifications section */}
                  {user && notifications.length === 0 && activePromos.length === 0 && (
                    <div className="p-4 text-center">
                      <Bell className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                      <p className="text-sm c4t-desc">No notifications yet</p>
                    </div>
                  )}
                  {user && notifications.length > 0 && (
                    <>
                      <div className="bg-black px-4 py-3 flex items-center gap-2">
                        <Bell className="w-4 h-4 text-white" />
                        <span className="font-['Fredoka'] font-bold text-white text-lg">Notifications</span>
                      </div>
                      <div className="p-3 space-y-2 max-h-60 overflow-y-auto">
                        {notifications.map(n => (
                          <div key={n.id} className={`border-2 border-black rounded-xl px-3 py-2 ${n.is_read ? "bg-white" : "bg-[#FFF9E6]"}`}>
                            <p className="font-bold text-sm text-black">{n.subject}</p>
                            <p className="text-xs text-gray-600 mt-0.5">{n.message}</p>
                            <p className="text-xs text-gray-400 mt-1">{new Date(n.created_at).toLocaleDateString()}</p>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {/* Active promos section */}
                  {activePromos.length > 0 && (
                    <>
                      <div className="bg-[#FF1F87] px-4 py-3 flex items-center gap-2">
                        <Bell className="w-4 h-4 text-white" />
                        <span className="font-['Fredoka'] font-bold text-white text-lg">Active Deals</span>
                      </div>
                      <div className="p-3 space-y-2">
                        {activePromos.map(p => (
                          <div key={p.code} className="flex items-center justify-between bg-[#FFF9E6] border-2 border-black rounded-xl px-3 py-2">
                            <div>
                              <p className="font-black text-lg tracking-widest text-[#FF1F87]">{p.code}</p>
                              <p className="text-xs font-semibold text-black">{p.percent_off}% off your order</p>
                            </div>
                            <Link to="/shop" className="c4t-btn c4t-btn-pink !text-xs !py-1 !px-3">Shop</Link>
                          </div>
                        ))}
                        <p className="text-xs c4t-desc text-center pt-1">Enter code at checkout ✨</p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          <Link to="/cart" className="relative c4t-pill" data-testid="nav-cart-button">
            <ShoppingCart className="w-5 h-5" strokeWidth={2.5} />
            <span className="hidden sm:inline">Cart</span>
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#FF1F87] text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center border-2 border-black" data-testid="cart-count-badge">
                {count}
              </span>
            )}
          </Link>
          <button className="md:hidden c4t-pill !p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t-2 border-black bg-white px-4 py-3 space-y-2">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"}
              className={({ isActive }) => `block font-['Fredoka'] font-semibold text-lg py-2 ${isActive ? "text-[#FF1F87]" : "text-black"}`}
              onClick={() => setOpen(false)}>{n.label}</NavLink>
          ))}
          {user && (
            <Link to="/account" onClick={() => setOpen(false)} className="block font-['Fredoka'] font-semibold text-lg py-2 text-black">My Orders</Link>
          )}
        </div>
      )}
    </header>
  );
};

export const Footer = () => {
  const { t, settings } = useSettings();
  return (
    <footer className="mt-20 border-t-[3px] border-black bg-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 py-10 grid md:grid-cols-3 gap-8">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <img src={LOGO_URL} alt="logo" className="h-10 w-10 logo-blend" />
            <span className="font-['Fredoka'] font-bold text-xl">Cheap4Teach</span>
          </div>
          <p className="c4t-desc">{t("footer.tagline", "Affordable classroom essentials made for Caribbean teachers.")}</p>
        </div>
        <div>
          <h4 className="font-bold mb-3 text-lg">Quick Links</h4>
          <ul className="space-y-1 text-black font-semibold">
            <li><Link to="/shop" className="hover:text-[#FF1F87]">Shop All</Link></li>
            <li><Link to="/digital-shop" className="hover:text-[#FF1F87]">Digital Shop</Link></li>
            <li><Link to="/shipping" className="hover:text-[#FF1F87]">Shipping Info</Link></li>
            <li><Link to="/about" className="hover:text-[#FF1F87]">About Us</Link></li>
            <li><Link to="/contact" className="hover:text-[#FF1F87]">Contact</Link></li>
            <li><Link to="/admin" className="hover:text-[#FF1F87] text-xs">Admin</Link></li>
            <li><Link to="/privacy" className="hover:text-[#FF1F87]">Privacy Policy</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-bold mb-3 text-lg">Stay in Touch</h4>
          <p className="flex items-center gap-2 text-black font-semibold"><Mail className="w-4 h-4" /> {settings?.contact_email || "cheap4teach@gmail.com"}</p>
          <p className="flex items-center gap-2 text-black font-semibold mt-1"><Phone className="w-4 h-4" /> WhatsApp: {settings?.whatsapp || "876 319 1763"}</p>
          <p className="flex items-center gap-2 mt-3 text-[#FF1F87] font-bold">
            <Heart className="w-4 h-4 fill-[#FF1F87]" /> {t("footer.heart_text", "Made with love for teachers")}
          </p>
        </div>
      </div>
      <div className="text-center py-4 text-sm text-black font-semibold border-t border-black/20">
        <Sparkles className="inline w-4 h-4 mr-1 text-[#FFC500]" /> © {new Date().getFullYear()} Cheap4Teach. All rights reserved. · <Link to="/privacy" className="underline hover:text-[#FF1F87]">Privacy Policy</Link>
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  const { settings } = useSettings();
  const wa = (settings?.whatsapp || "876 319 1763").replace(/\D/g, "");
  const waUrl = `https://wa.me/${wa.startsWith("1") ? wa : "1" + wa}?text=${encodeURIComponent("Hi Cheap4Teach! I have a question 👋")}`;
  const announcementBar = settings?.announcement_bar || "";
  return (
    <div className="min-h-screen flex flex-col">
      {announcementBar && (
        <div className="bg-[#FEFCE8] border-b-[3px] border-black overflow-hidden" data-testid="announcement-bar">
          <style>{`
            @keyframes c4t-marquee {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .c4t-marquee-track {
              display: inline-flex;
              animation: c4t-marquee 28s linear infinite;
              white-space: nowrap;
            }
          `}</style>
          <div className="c4t-marquee-track py-2 items-center">
            {[...Array(8)].map((_, i) => (
              <span key={i} className="mx-8 text-black font-['Fredoka'] font-semibold tracking-wide text-base">
                {announcementBar.split("\n").map((line, li) => (
                  <React.Fragment key={li}>{line}{li < announcementBar.split("\n").length - 1 ? " • " : ""}</React.Fragment>
                ))}
                <span className="mx-4 font-black text-black"> • </span>
              </span>
            ))}
          </div>
        </div>
      )}
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
      <a
        href={waUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with us on WhatsApp"
        data-testid="floating-whatsapp-btn"
        className="fixed bottom-6 right-6 z-50 group flex items-center gap-2 bg-[#25D366] text-white rounded-full border-[3px] border-black shadow-[4px_4px_0_#000] hover:shadow-[2px_2px_0_#000] hover:translate-x-[2px] hover:translate-y-[2px] transition-all px-4 py-3 font-['Fredoka'] font-bold"
      >
        <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
          <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
        </svg>
        <span className="hidden sm:inline pr-1 text-sm">Chat</span>
      </a>
    </div>
  );
};

export default Layout;
